import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { setToken } from "@/lib/api";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/** Handles redirect from auth.emergentagent.com.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 *
 * This is the "middle page" every Google sign-in passes through — it renders
 * standalone (no header/footer), so it carries the SonSpeak logo and the
 * "Built with Emergent" credit itself (contest requirement).
 */
export default function AuthCallback() {
  const location = useLocation();
  const nav = useNavigate();
  const { refresh } = useAuth();

  useEffect(() => {
    const hash = location.hash || window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      nav("/login", { replace: true });
      return;
    }
    const session_id = decodeURIComponent(m[1]);
    (async () => {
      try {
        const { data } = await api.post("/auth/google", { session_id });
        setToken(data.access_token);
        // Strip the hash BEFORE refresh(). AuthContext.refresh() early-returns
        // whenever `window.location.hash` still contains "session_id=" — that
        // race is the reason the user landed on /login post-Google previously
        // (refresh short-circuited, user stayed null, ProtectedRoute bounced
        // to /login, /login itself checks `user` which was still null).
        window.history.replaceState({}, "", "/pick-board");
        await refresh();
        nav("/pick-board", { replace: true });
      } catch (e) {
        const err = formatApiErrorDetail(e.response?.data?.detail) || e.message;
        nav("/login", { replace: true, state: { error: err } });
      }
    })();
  }, [location, nav, refresh]);

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center gap-5 px-4 bg-[color:var(--cream)]"
      data-testid="auth-callback"
    >
      <img
        src="/brand/sonspeak-logo-transparent.png"
        alt="SonSpeak"
        className="h-20 w-auto"
        data-testid="auth-callback-logo"
      />
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          className="inline-block w-6 h-6 border-[3px] border-[color:var(--ink)] border-t-transparent rounded-full animate-spin"
        />
        <div className="display text-2xl text-[color:var(--ink)]">Signing you in…</div>
      </div>
      <div className="text-sm text-[color:var(--ink-soft)]">
        Finishing your Google sign-in — one moment.
      </div>
      <a
        href="https://emergent.sh"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-xs text-[color:var(--ink-soft)] hover:text-[color:var(--ink)] transition-colors mt-4"
        data-testid="auth-callback-emergent-badge"
      >
        <span aria-hidden className="inline-block w-2 h-2 rounded-full bg-[color:var(--gold)]" />
        Built with Emergent
      </a>
    </div>
  );
}
